import React, { useState } from 'react'
import {
  User,
  Building,
  Globe,
  Save,
  Check,
  AlertCircle,
  Lock,
  Mail,
  Phone,
  X,
  CreditCard,
  Eye,
  EyeOff,
  CheckCircle2,
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { useAuth } from '../../context/AuthContext'
import LoadingSpinner from '../common/LoadingSpinner'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { cn } from '../../utils/cn'

const ClientSettings = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState({
    company_name: '',
    domain: '',
    email: '',
    phone: '',
    address: '',
    description: ''
  })
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [show2FAModal, setShow2FAModal] = useState(false)

  // Stripe key state
  const [stripeKey, setStripeKey] = useState('')
  const [showStripeKey, setShowStripeKey] = useState(false)

  const { data: plansRaw } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(r => r.data),
    { staleTime: 10 * 60 * 1000 }
  )

  // Fetch client profile
  const { data: profile, isLoading } = useQuery(
    'client-profile',
    () => apiService.get('/clients/profile').then(res => res.data),
    {
      onSuccess: (data) => {
        setFormData({
          company_name: data.company_name || '',
          domain: data.domain || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          description: data.description || ''
        })
      }
    }
  )

  // Fetch security information
  const { data: securityInfo, isLoading: securityLoading } = useQuery(
    'user-security',
    () => apiService.get('/auth/security').then(res => res.data),
    {
      staleTime: 5 * 60 * 1000 // 5 minutes
    }
  )

  // Update profile mutation
  const updateProfileMutation = useMutation(
    async (profileData) => {
      // Update the profile
      const response = await apiService.put('/clients/profile', profileData)
      
      // Mark about section as completed in onboarding
      await apiService.post('/clients/onboarding/update', {
        itemId: 'about',
        completed: true
      })
      
      // Mark domain as connected if domain was provided
      if (profileData.domain && profileData.domain.trim()) {
        await apiService.post('/clients/onboarding/update', {
          itemId: 'domain',
          completed: true
        })
      }
      
      return response
    },
    {
      onSuccess: () => {
        toast.success('Settings saved successfully!')
        queryClient.invalidateQueries('client-profile')
        queryClient.invalidateQueries('onboarding-checklist')
        queryClient.invalidateQueries('user-security')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to save settings')
      }
    }
  )

  // Stripe status query (never returns the key itself)
  const { data: stripeStatus, refetch: refetchStripe } = useQuery(
    'stripe-status',
    () => apiService.get('/clients/stripe').then(r => r.data),
    { staleTime: 60 * 1000 }
  )

  const saveStripeMutation = useMutation(
    (key) => apiService.put('/clients/stripe', { stripeSecretKey: key || null }),
    {
      onSuccess: () => {
        toast.success(stripeKey ? 'Stripe key saved' : 'Stripe key removed')
        setStripeKey('')
        refetchStripe()
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to save Stripe key'),
    }
  )

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    updateProfileMutation.mutate(formData)
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" text="Loading settings..." />
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-50">
          Account Settings
        </h1>
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          Manage your company information and account preferences
        </p>
      </div>

      {/* Profile Information */}
      <Card className="border-neutral-200 dark:border-[#2f2b41]">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Building className="w-5 h-5 text-accent-600 dark:text-accent-400" />
            <span>Company Information</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Company Name */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-[#2f2b41] rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100"
                  placeholder="Enter your company name"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-3 py-2 border border-neutral-200 dark:border-[#2f2b41] rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100"
                    placeholder="company@example.com"
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-3 py-2 border border-neutral-200 dark:border-[#2f2b41] rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              {/* Domain */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Website Domain
                </label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <input
                    type="url"
                    name="domain"
                    value={formData.domain}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-3 py-2 border border-neutral-200 dark:border-[#2f2b41] rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100"
                    placeholder="https://yourwebsite.com"
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Business Address
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-neutral-200 dark:border-[#2f2b41] rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100"
                placeholder="123 Business St, City, State 12345"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Company Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 border border-neutral-200 dark:border-[#2f2b41] rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100"
                placeholder="Tell us about your company..."
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={updateProfileMutation.isLoading}
              >
                {updateProfileMutation.isLoading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>

        </CardContent>
      </Card>

      {/* Account Security */}
      <Card className="border-neutral-200 dark:border-[#2f2b41]">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Lock className="w-5 h-5 text-accent-600 dark:text-accent-400" />
            <span>Account Security</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-neutral-50 dark:bg-[#201d2e] rounded-lg">
              <div>
                <h3 className="font-medium text-neutral-900 dark:text-neutral-50">
                  Password
                </h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  {securityLoading ? (
                    'Loading...'
                  ) : securityInfo?.password_changed_at ? (
                    `Last updated ${new Date(securityInfo.password_changed_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}`
                  ) : (
                    'Never changed'
                  )}
                </p>
              </div>
              <Button 
                variant="outline"
                onClick={() => setShowPasswordModal(true)}
              >
                Change Password
              </Button>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-neutral-50 dark:bg-[#201d2e] rounded-lg">
              <div>
                <h3 className="font-medium text-neutral-900 dark:text-neutral-50">
                  Two-Factor Authentication
                </h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  {securityInfo?.totp_enabled
                    ? 'Enabled — your account is protected with an authenticator app'
                    : 'Add an extra layer of security to your account'}
                </p>
              </div>
              <Button
                variant={securityInfo?.totp_enabled ? 'destructive' : 'outline'}
                onClick={() => setShow2FAModal(true)}
              >
                {securityInfo?.totp_enabled ? 'Disable 2FA' : 'Enable 2FA'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stripe Payments */}
      <Card className="border-neutral-200 dark:border-[#2f2b41]">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <CreditCard className="w-5 h-5 text-accent-600 dark:text-accent-400" />
            <span>Online Payments (Stripe)</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status badge */}
          <div className={cn(
            'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium w-fit',
            stripeStatus?.configured
              ? 'bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-300'
              : 'bg-neutral-100 dark:bg-[#201d2e] text-neutral-500 dark:text-neutral-400'
          )}>
            {stripeStatus?.configured
              ? <><CheckCircle2 className="w-4 h-4" /> Stripe connected</>
              : <><CreditCard className="w-4 h-4" /> Not configured</>}
          </div>

          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Enter your Stripe secret key to accept card payments on your website.
            Your key is stored securely and never shown again after saving.{' '}
            <a
              href="https://dashboard.stripe.com/apikeys"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-600 dark:text-accent-400 underline underline-offset-2"
            >
              Find your key →
            </a>
          </p>

          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type={showStripeKey ? 'text' : 'password'}
                value={stripeKey}
                onChange={e => setStripeKey(e.target.value)}
                placeholder={stripeStatus?.configured ? '••••••••••••••••••• (leave blank to keep current)' : 'sk_live_... or sk_test_...'}
                className="w-full px-3 py-2 pr-10 border border-neutral-200 dark:border-[#2f2b41] rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 font-mono text-sm"
              />
              <button
                type="button"
                onClick={() => setShowStripeKey(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
              >
                {showStripeKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <Button
              onClick={() => saveStripeMutation.mutate(stripeKey)}
              disabled={saveStripeMutation.isLoading || (!stripeKey && !stripeStatus?.configured)}
            >
              {saveStripeMutation.isLoading ? 'Saving…' : 'Save'}
            </Button>
            {stripeStatus?.configured && (
              <Button
                variant="outline"
                onClick={() => { if (window.confirm('Remove your Stripe key?')) saveStripeMutation.mutate('') }}
                disabled={saveStripeMutation.isLoading}
              >
                Remove
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Subscription Info */}
      <Card className="border-neutral-200 dark:border-[#2f2b41]">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <User className="w-5 h-5 text-accent-600 dark:text-accent-400" />
            <span>Subscription</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-neutral-900 dark:text-neutral-50">
                Current Plan: {plansRaw?.[profile?.subscription_plan]?.name || profile?.subscription_plan?.toUpperCase() || 'BASIC'}
              </h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                {profile?.edit_quota || 50} edits per month
              </p>
            </div>
            <Button 
              onClick={() => navigate('/client/billing')}
            >
              Upgrade Plan
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <PasswordChangeModal
          onClose={() => setShowPasswordModal(false)}
          queryClient={queryClient}
        />
      )}

      {/* 2FA Modal */}
      {show2FAModal && (
        <TwoFactorModal
          isEnabled={!!securityInfo?.totp_enabled}
          onClose={() => { setShow2FAModal(false); queryClient.invalidateQueries('user-security') }}
        />
      )}
    </div>
  )
}

// Password Change Modal Component
const PasswordChangeModal = ({ onClose, queryClient }) => {
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    
    if (passwords.newPassword !== passwords.confirmPassword) {
      setError('New passwords do not match')
      return
    }
    
    if (passwords.newPassword.length < 6) {
      setError('Password must be at least 6 characters long')
      return
    }

    setIsLoading(true)
    try {
      await apiService.put('/auth/password', {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword
      })
      setSuccess(true)
      toast.success('Password changed successfully!')
      queryClient.invalidateQueries('user-security')
      setTimeout(() => {
        onClose()
      }, 2000)
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to change password'
      setError(errorMsg)
      toast.error(errorMsg)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-black/30 dark:bg-black/60 backdrop-blur-sm transition-all" onClick={onClose} />

        <Card className="inline-block align-bottom text-left overflow-hidden shadow-2xl border-0 transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full bg-white dark:bg-[#1a1625]">
          <form onSubmit={handleSubmit}>
            <CardHeader className="px-6 pt-6 pb-4">
              <div className="flex items-center justify-between mb-4">
                <CardTitle className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">
                  Change Password
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={onClose}>
                  <X className="h-6 w-6" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="px-6 pb-4">

              {success ? (
                <div className="text-center py-4">
                  <Check className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <p className="text-green-600">Password changed successfully!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Current Password
                    </label>
                    <input
                      type="password"
                      value={passwords.currentPassword}
                      onChange={(e) => setPasswords(prev => ({ ...prev, currentPassword: e.target.value }))}
                      className="w-full px-3 py-2 border border-neutral-200 dark:border-[#2f2b41] rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      New Password
                    </label>
                    <input
                      type="password"
                      value={passwords.newPassword}
                      onChange={(e) => setPasswords(prev => ({ ...prev, newPassword: e.target.value }))}
                      className="w-full px-3 py-2 border border-neutral-200 dark:border-[#2f2b41] rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100"
                      required
                      minLength={6}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      value={passwords.confirmPassword}
                      onChange={(e) => setPasswords(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      className="w-full px-3 py-2 border border-neutral-200 dark:border-[#2f2b41] rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100"
                      required
                      minLength={6}
                    />
                  </div>

                  {error && (
                    <div className="p-3 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-700 rounded-md">
                      <div className="flex items-center">
                        <AlertCircle className="h-4 w-4 text-error-500 mr-2" />
                        <span className="text-sm text-error-700 dark:text-error-200">{error}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>

            {!success && (
              <div className="bg-neutral-50/50 dark:bg-[#1f1c2c] border-t border-neutral-200 dark:border-[#2f2b41] px-6 py-3 flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Changing...
                    </>
                  ) : (
                    'Change Password'
                  )}
                </Button>
              </div>
            )}
          </form>
        </Card>
      </div>
    </div>
  )
}

// Two Factor Authentication Modal Component
const TwoFactorModal = ({ isEnabled, onClose }) => {
  const [step, setStep] = useState(isEnabled ? 'disable' : 'info')
  const [qrCode, setQrCode] = useState('')
  const [secret, setSecret] = useState('')
  const [code, setCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const inputClass = "w-full px-3 py-2 border border-neutral-200 dark:border-[#2f2b41] rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 text-center text-xl tracking-widest"

  const handleSetup = async () => {
    setIsLoading(true)
    setError('')
    try {
      const response = await apiService.post('/auth/2fa/setup')
      setQrCode(response.data.qrCode)
      setSecret(response.data.secret)
      setStep('setup')
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to setup 2FA')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerify = async (e) => {
    e?.preventDefault()
    setIsLoading(true)
    setError('')
    try {
      await apiService.post('/auth/2fa/verify', { token: code })
      setStep('success')
      toast.success('2FA enabled successfully!')
      setTimeout(onClose, 2000)
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid verification code')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDisable = async (e) => {
    e?.preventDefault()
    setIsLoading(true)
    setError('')
    try {
      await apiService.post('/auth/2fa/disable', { token: code })
      setStep('disabled')
      toast.success('2FA disabled')
      setTimeout(onClose, 2000)
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid authenticator code')
    } finally {
      setIsLoading(false)
    }
  }

  const ErrorBox = ({ msg }) => msg ? (
    <div className="p-3 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-700 rounded-md flex items-center gap-2">
      <AlertCircle className="h-4 w-4 text-error-500 shrink-0" />
      <span className="text-sm text-error-700 dark:text-error-200">{msg}</span>
    </div>
  ) : null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-black/30 dark:bg-black/60 backdrop-blur-sm" onClick={onClose} />

        <Card className="inline-block align-bottom text-left overflow-hidden shadow-2xl border-0 transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full bg-white dark:bg-[#1a1625]">
          <CardHeader className="px-6 pt-6 pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">
                Two-Factor Authentication
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={onClose}><X className="h-5 w-5" /></Button>
            </div>
          </CardHeader>

          <CardContent className="px-6 pb-4 space-y-4">

            {/* ── Info step ── */}
            {step === 'info' && (
              <>
                <p className="text-neutral-600 dark:text-neutral-400">
                  Adds an extra layer of security — you'll need a code from your authenticator app each time you log in.
                </p>
                <ul className="list-disc list-inside text-sm text-neutral-600 dark:text-neutral-400 space-y-1">
                  <li>Google Authenticator</li>
                  <li>Authy</li>
                  <li>Any TOTP-compatible app</li>
                </ul>
                <ErrorBox msg={error} />
              </>
            )}

            {/* ── Setup step: show QR + manual secret ── */}
            {step === 'setup' && (
              <>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  Scan this QR code with your authenticator app, then enter the 6-digit code to confirm.
                </p>
                {qrCode && (
                  <div className="flex justify-center p-4 bg-white rounded-xl border border-neutral-200 dark:border-[#2f2b41]">
                    <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" />
                  </div>
                )}
                <div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Can't scan? Enter this key manually:</p>
                  <code className="block p-2 bg-neutral-100 dark:bg-[#201d2e] rounded text-xs break-all font-mono">
                    {secret}
                  </code>
                </div>
                <form onSubmit={handleVerify} className="space-y-3">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className={inputClass}
                    placeholder="000000"
                    autoFocus
                    required
                  />
                  <ErrorBox msg={error} />
                </form>
              </>
            )}

            {/* ── Disable step ── */}
            {step === 'disable' && (
              <>
                <p className="text-neutral-600 dark:text-neutral-400">
                  Enter the current 6-digit code from your authenticator app to disable 2FA.
                </p>
                <form onSubmit={handleDisable} className="space-y-3">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className={inputClass}
                    placeholder="000000"
                    autoFocus
                    required
                  />
                  <ErrorBox msg={error} />
                </form>
              </>
            )}

            {/* ── Success / disabled ── */}
            {(step === 'success' || step === 'disabled') && (
              <div className="text-center py-4 space-y-2">
                <Check className="h-12 w-12 text-success-500 mx-auto" />
                <p className="font-medium text-neutral-900 dark:text-neutral-50">
                  {step === 'success' ? '2FA enabled successfully!' : '2FA has been disabled'}
                </p>
              </div>
            )}

          </CardContent>

          {!['success', 'disabled'].includes(step) && (
            <div className="bg-neutral-50/50 dark:bg-[#1f1c2c] border-t border-neutral-200 dark:border-[#2f2b41] px-6 py-3 flex justify-end space-x-3">
              {step === 'info' && (
                <>
                  <Button variant="outline" onClick={onClose}>Cancel</Button>
                  <Button onClick={handleSetup} disabled={isLoading}>
                    {isLoading ? <><LoadingSpinner size="sm" className="mr-2" />Loading…</> : 'Get Started'}
                  </Button>
                </>
              )}
              {step === 'setup' && (
                <>
                  <Button variant="outline" onClick={() => setStep('info')}>Back</Button>
                  <Button onClick={handleVerify} disabled={isLoading || code.length !== 6}>
                    {isLoading ? <><LoadingSpinner size="sm" className="mr-2" />Verifying…</> : 'Verify & Enable'}
                  </Button>
                </>
              )}
              {step === 'disable' && (
                <>
                  <Button variant="outline" onClick={onClose}>Cancel</Button>
                  <Button variant="destructive" onClick={handleDisable} disabled={isLoading || code.length !== 6}>
                    {isLoading ? <><LoadingSpinner size="sm" className="mr-2" />Disabling…</> : 'Disable 2FA'}
                  </Button>
                </>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}

export default ClientSettings
